import { useFormContext } from 'react-hook-form';
import type { ReactNode } from 'react';
import type { TLocalize } from '@hooks/use-localize';
import type { INowFormValues, INowModel } from '@/types/now';
import { WhatIsInvolvedSection, WhyDonateThroughNkrSection, WhyLivingDonationSection } from '@views/register/now-sections/dynamic-reg-sections';
import {
    AddressLine1Field,
    CityField,
    CountryOfResidenceField,
    DonationTypeField,
    DonorMentorOptInField,
    EmailField,
    FirstNameField,
    HipaaField,
    InternationalPhoneField,
    LastNameField,
    MobilePhoneField,
    PostalCodeField,
    ResidenceSection,
    ResidesInUsaField,
    StateField,
    TextOptInNotice,
} from '@views/register/now-sections/now-fields';

interface IProps {
    model: INowModel;
    t: TLocalize;
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    isSubmitting: boolean;
    submitError: string | null;
    alerts: ReactNode;
}

const scrollToRegistrationForm = () =>
    document.getElementById('registration-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const DynamicNow = ({ model, t, onSubmit, isSubmitting, submitError, alerts }: IProps) => {
    const isResidingInUsa = useFormContext<INowFormValues>().watch('isResidingInUsa');

    const hasRibbonMedia = !!model.ribbonTwoMediaUrl;
    const hasRibbonCopy = !!model.ribbonTwoIntroHtml || !!model.ribbonTwoBodyHtml;
    const hasRibbonSection = !!model.ribbonTwoTitleHtml || hasRibbonCopy || hasRibbonMedia;

    return (
        <>
        <style>{`
        header { 
            position: fixed; 
            padding: 1.0625rem 1.125rem;
        }

        .nkr-main {
            padding-top: 75px;
        }

        .radio-inline {
            font-weight: 400 !important;
        }
        `}</style>
        <div>
            {model.heroImageUrl && (
                <div
                    className={'hero ' + (model.isGenericRegPage ? 'hero--message' : 'hero--image')}
                    style={{ backgroundImage: `url('${model.heroImageUrl}')` }}
                >
                    {model.heroTextOverlay && (
                        <div className="hero-overlay-wrap">
                            <div className="dynamic-inner-wrap">
                                <p className={`hero-overlay-text hero-overlay-text--${model.heroTextOverlayAlignment}`}>
                                    {model.heroTextOverlay}
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={scrollToRegistrationForm}
                                >
                                    {t('DashLabel_GetStarted', 'Get Started')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {alerts}

            <section id="registration-form-section" className="section registration-form-section">
                {model.ribbonTwoTitleHtml && (
                    <h1
                        className="section-title"
                        dangerouslySetInnerHTML={{ __html: model.ribbonTwoTitleHtml }}
                    />
                )}

                <div className="row form-container form-wrapper">
                    {hasRibbonSection && (
                        <div className="col-md-4">
                            <div className="ribbon-two-container">
                                {(hasRibbonMedia || hasRibbonCopy) && (
                                    <>
                                        {hasRibbonMedia && (
                                            <div
                                                className="ribbon-two-img-container"
                                                style={{ backgroundImage: `url('${model.ribbonTwoMediaUrl}')` }}
                                            ></div>
                                        )}
                                        <div className="ribbon-two-copy">
                                            {model.ribbonTwoIntroHtml && (
                                                <p
                                                    className="lead ribbon-two-intro"
                                                    dangerouslySetInnerHTML={{ __html: model.ribbonTwoIntroHtml }}
                                                />
                                            )}
                                            {model.ribbonTwoBodyHtml && (
                                                <p
                                                    className="ribbon-two-body"
                                                    dangerouslySetInnerHTML={{ __html: model.ribbonTwoBodyHtml }}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="col-md-8">
                        <form id="RegistrationForm" className="form-horzontal" onSubmit={onSubmit}>
                            <div className="row">
                                <FirstNameField t={t} className="form-group col-md-5" />
                                <LastNameField t={t} className="form-group col-md-5" />
                            </div>

                            <div className="row">
                                <ResidesInUsaField t={t} className="form-group col-md-5" />
                            </div>

                            <ResidenceSection id="non-us-residence" show={isResidingInUsa === '0'}>
                                <div className="row">
                                    <CountryOfResidenceField
                                        t={t}
                                        className="form-group col-md-6"
                                        countries={model.countries}
                                    />
                                    <InternationalPhoneField t={t} className="form-group col-md-5" />
                                </div>
                            </ResidenceSection>

                            <ResidenceSection id="us-residence" show={isResidingInUsa === '1'}>
                                <div className="row">
                                    <AddressLine1Field t={t} className="form-group col-md-8" />
                                </div>

                                <div className="row">
                                    <PostalCodeField t={t} className="form-group col-md-4" />
                                    <CityField t={t} className="form-group col-md-4" />
                                    <StateField t={t} className="form-group col-md-4" states={model.states} />
                                </div>

                                <div className='row'>
                                    <MobilePhoneField t={t} className="form-group col-md-4" />
                                </div>

                                <TextOptInNotice t={t} className="text-opt-in" />
                            </ResidenceSection>

                            <div className="row">
                                <EmailField t={t} className="form-group col-md-5" />
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <HipaaField t={t} labelClassName="" />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-12">
                                    <DonorMentorOptInField t={t} />
                                </div>
                            </div>

                            {model.isDashLiverAvailable && (
                                <DonationTypeField t={t} donationTypes={model.donationTypes} />
                            )}
                        </form>

                        {submitError && <div className="alert alert-danger">{submitError}</div>}

                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <button
                                className="btn btn-primary btn-lg"
                                type="submit"
                                form="RegistrationForm"
                                disabled={isSubmitting}
                            >
                                {t('DashLabel_SubmitForm', 'Submit Form')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <WhyLivingDonationSection />
            <WhyDonateThroughNkrSection />
            <WhatIsInvolvedSection />
        </div>
        </>
    );
};

export default DynamicNow;
